import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatures } from '../../hooks/useFeatures';
import { analyticsAllowed, prefersNoTracking, setAnalyticsActive, splitUtm, trackPageview, umamiConfig } from '../../utils/analytics';
const SCRIPT_ID = 'umami-script';

/**
 * Laadt Umami (zelf gehost) op de publieke site en telt paginawissels in de SPA. Laadt niets zonder
 * configuratie, met het onderdeel uit, bij Do Not Track, of als je binnenkomt op een afgeschermde
 * pagina. De automatische telling van Umami staat uit: de site telt zelf, en slaat de afgeschermde
 * pagina's over. `utm_source` wordt één keer gelezen en dan uit de adresbalk gehaald.
 */
export function AnalyticsScript() {
  const {
    pathname,
    search,
    hash,
    state
  } = useLocation();
  const navigate = useNavigate();
  const {
    analytics
  } = useFeatures();
  const config = useMemo(() => umamiConfig(), []);
  const enabled = analytics && config !== null && !prefersNoTracking();
  const [ready, setReady] = useState(false);
  /** utm van de link waarmee iemand binnenkwam: gaat mee met de eerstvolgende paginaweergave. */
  const pendingUtm = useRef('');

  // 1. utm lezen en uit de adresbalk halen (altijd, ook zonder statistiek: geen dubbele pagina's).
  useEffect(() => {
    if (!/[?&]utm_/i.test(search)) return;
    const {
      utm,
      rest
    } = splitUtm(search);
    if (utm) pendingUtm.current = utm;
    navigate({
      pathname,
      search: rest,
      hash
    }, {
      replace: true,
      state
    });
  }, [search, pathname, hash, state, navigate]);

  // 2. Het script, één keer, alleen als je op een publieke pagina bent.
  const allowedHere = analyticsAllowed(pathname);
  useEffect(() => {
    if (!enabled || !config || !allowedHere || document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.defer = true;
    s.src = config.src;
    s.dataset.websiteId = config.websiteId;
    s.dataset.domains = config.domains;
    s.dataset.doNotTrack = 'true';
    s.dataset.autoTrack = 'false';
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, [enabled, config, allowedHere]);

  // Onderdeel uit (na opslaan in het beheer): meteen niets meer versturen.
  useEffect(() => {
    setAnalyticsActive(enabled && ready);
    return () => setAnalyticsActive(false);
  }, [enabled, ready]);

  // 3. Elke paginawissel op de publieke site telt één keer.
  useEffect(() => {
    if (!enabled || !ready || !analyticsAllowed(pathname)) return;
    const url = pendingUtm.current ? `${pathname}?${pendingUtm.current}` : pathname;
    pendingUtm.current = '';
    trackPageview(url);
  }, [pathname, enabled, ready]);
  return null;
}